import { useEffect, useMemo, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { Layout } from "@/shared/layout/layout";
import { PrivateRoute } from "@/shared/privateRoute";
import { ErrorBoundary } from "@/shared/components/errorBoundary";
import { ScrollToTop } from "@/shared/components/scrollToTop";
import { PageLoading } from "@/shared/ui/pageLoading";
import { ToastProvider, useToast } from "@/shared/ui/toast";
import axios, { AxiosError } from "axios";

const LoginPage = lazy(() =>
  import("@/pages/login/loginPage").then((m) => ({ default: m.LoginPage })),
);
const WelcomePage = lazy(() =>
  import("@/pages/welcome/welcomePage").then((m) => ({
    default: m.WelcomePage,
  })),
);
const ContactsPage = lazy(() =>
  import("@/pages/contacts/contactsPage").then((m) => ({
    default: m.ContactsPage,
  })),
);
const ClientsPage = lazy(() =>
  import("@/pages/clients/clientsPage").then((m) => ({
    default: m.ClientsPage,
  })),
);
const ProjectsPage = lazy(() =>
  import("@/pages/projects/projectsPage").then((m) => ({
    default: m.ProjectsPage,
  })),
);
const ProjectPage = lazy(() =>
  import("@/pages/project/projectPage").then((m) => ({
    default: m.ProjectPage,
  })),
);
const ClientPage = lazy(() =>
  import("@/pages/client/clientPage").then((m) => ({ default: m.ClientPage })),
);
const InsightsPage = lazy(() =>
  import("@/pages/insights/insightsPage").then((m) => ({
    default: m.InsightsPage,
  })),
);
const TermsPage = lazy(() =>
  import("@/pages/terms/termsPage").then((m) => ({ default: m.TermsPage })),
);

const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    // Handle 429 rate limit errors
    if (error.response?.status === 429) {
      return "Too many requests. Please slow down and try again in a moment.";
    }

    // Extract error message from FastAPI's detail field
    if (error.response?.data) {
      const data = error.response.data as {
        detail?: string | { msg?: string }[];
      };
      if (typeof data.detail === "string") {
        return data.detail;
      }
      if (Array.isArray(data.detail)) {
        return data.detail.map((e) => e.msg || "Validation error").join(", ");
      }
    }
    return error.message || "An error occurred";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
};

// Inner component that has access to toast context
function AppContent() {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const { showToast } = useToast();

  // Listen for toast events (from axios interceptor and React Query)
  useEffect(() => {
    const handleShowToast = (
      event: CustomEvent<{
        message: string;
        variant: "default" | "destructive" | "success";
      }>,
    ) => {
      showToast(event.detail.message, event.detail.variant);
    };

    window.addEventListener("show-toast", handleShowToast as EventListener);
    return () => {
      window.removeEventListener(
        "show-toast",
        handleShowToast as EventListener,
      );
    };
  }, [showToast]);

  // Create QueryClient - use custom event for errors to avoid ref complexity
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
          },
          mutations: {
            onError: (error: unknown) => {
              // Skip 429 errors - handled by axios interceptor
              if (
                error instanceof AxiosError &&
                error.response?.status === 429
              ) {
                return;
              }
              // Dispatch event for other mutation errors (from mutate() calls)
              const message = getErrorMessage(error);
              window.dispatchEvent(
                new CustomEvent("show-toast", {
                  detail: { message, variant: "destructive" },
                }),
              );
            },
          },
        },
      }),
    [],
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/share/:token" element={<ProjectPage />} />

              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="/projects" replace />} />
                <Route path="projects/:id/shotlist" element={<ProjectPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="clients/:clientId" element={<ClientPage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="insights" element={<InsightsPage />} />
                <Route path="*" element={<Navigate to="/projects" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    </QueryClientProvider>
  );
}

function App() {
  axios.defaults.withCredentials = true;

  // Set up axios interceptor to catch 429 errors globally
  // This is necessary because mutateAsync() errors that aren't caught become unhandled
  // promise rejections, which bypass React Query's onError handler
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 429) {
          window.dispatchEvent(
            new CustomEvent("show-toast", {
              detail: {
                message:
                  "Too many requests. Please slow down and try again in a moment.",
                variant: "destructive",
              },
            }),
          );
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;
